import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
    title: 'Bug & Issue Tracking',
    description: 'Log, prioritise, and resolve bugs fast. Assign severity levels, link issues to sprints, and never let a defect slip through.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
    title: 'Project Management',
    description: 'Organise work into projects, track progress at a glance, and keep every sprint on schedule with a clean dashboard.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'Team Collaboration',
    description: 'Invite developers, testers, and stakeholders by email. Role-based access keeps the right people on the right projects.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
    title: 'Task Workflows',
    description: 'Move issues through Open → In Progress → Closed with one click. Custom workflows adapt to your team\'s process.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
      </svg>
    ),
    title: 'Rich Text Descriptions',
    description: 'Write detailed issue reports with bold, lists, code snippets, and more using the built-in rich text editor.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="24" height="24">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1" /><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" /><line x1="6" y1="1" x2="6" y2="4" /><line x1="10" y1="1" x2="10" y2="4" /><line x1="14" y1="1" x2="14" y2="4" />
      </svg>
    ),
    title: 'Real-Time Notifications',
    description: 'Get instant alerts when issues are assigned, updated, or resolved. Stay in the loop without checking the board constantly.',
  },
];

const steps = [
  { num: '01', title: 'Create a project', body: 'Set up a project in seconds — add a name, description, and invite your team.' },
  { num: '02', title: 'Log issues & assign them', body: 'Create detailed bug reports, set priority and severity, and assign developers and testers.' },
  { num: '03', title: 'Track progress & ship', body: 'Move issues through your workflow, monitor sprint progress, and ship software on time.' },
];

const BrandLogo = () => (
  <svg viewBox="0 0 32 32" width="22" height="22" aria-hidden="true">
    <defs>
      <linearGradient id="lp-grad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#1C9B82"/><stop offset="100%" stopColor="#00796B"/>
      </linearGradient>
    </defs>
    <rect width="32" height="32" rx="7" fill="url(#lp-grad2)"/>
    <polyline points="7.5,17 13,22.5 24.5,10.5" fill="none" stroke="white" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface HelpForm { name: string; email: string; subject: string; message: string; }

function HelpModal({ onClose }: { onClose: () => void }) {
  const [form, setForm]     = useState<HelpForm>({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrMsg('');
    try {
      const res = await fetch('/api/help', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send');
      setStatus('sent');
    } catch (err: any) {
      setErrMsg(err.message);
      setStatus('error');
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Request Help</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {status === 'sent' ? (
          <div className="modal-body" style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h3 style={{ margin: '0 0 8px', color: 'var(--color-text)' }}>Message sent!</h3>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 15 }}>
              We'll get back to you at <strong>{form.email}</strong> as soon as possible.
            </p>
            <button className="btn-primary" style={{ marginTop: 24 }} onClick={onClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Your Name</label>
                <input className="form-input" name="name" value={form.name} onChange={handleChange} required placeholder="John Doe" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} required placeholder="you@example.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Subject</label>
                <select className="form-select" name="subject" value={form.subject} onChange={handleChange}>
                  <option value="">Select a topic…</option>
                  <option value="Getting started">Getting started</option>
                  <option value="Account & billing">Account &amp; billing</option>
                  <option value="Bug / technical issue">Bug / technical issue</option>
                  <option value="Feature request">Feature request</option>
                  <option value="Team & permissions">Team &amp; permissions</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea className="form-textarea" name="message" value={form.message} onChange={handleChange} required placeholder="Describe your issue or question in detail…" rows={5} />
              </div>
              {status === 'error' && (
                <div className="error-msg" style={{ marginBottom: 12 }}>{errMsg}</div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="landing">
      {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="landing-hero" id="hero">
        <div className="landing-hero-inner">
          <div className="landing-badge">Free · No credit card required</div>
          <h1 className="landing-headline">
            Track bugs.<br />
            Manage projects.<br />
            <span className="landing-headline-accent">Ship software faster.</span>
          </h1>
          <p className="landing-subheading">
            Naipeak Issue Tracker is the agile bug tracking and project management platform built for modern software development teams.
            Log issues, assign developers, run sprints, and close bugs — all in one place.
          </p>
          <div className="landing-cta-group">
            <Link to="/signup" className="landing-btn-primary">Get started free</Link>
            <Link to="/login"  className="landing-btn-secondary">Log in</Link>
          </div>
          <p className="landing-cta-note">Trusted by agile teams · Works in any browser · Free forever</p>
        </div>

        {/* Hero visual */}
        <div className="landing-hero-visual" aria-hidden="true">
          <div className="hero-card">
            <div className="hero-card-header">
              <div className="hero-card-dot red" /><div className="hero-card-dot yellow" /><div className="hero-card-dot green" />
              <span className="hero-card-title">Issue Tracker — Dashboard</span>
            </div>
            <div className="hero-card-body">
              {[
                { label: 'Login page not redirecting after OAuth', status: 'In Progress', sev: 'High' },
                { label: 'Sprint burndown chart missing data', status: 'Open', sev: 'Medium' },
                { label: 'Email notifications delayed', status: 'Open', sev: 'Low' },
                { label: 'File upload limit not enforced', status: 'Closed', sev: 'High' },
              ].map((item, i) => (
                <div key={i} className="hero-issue-row">
                  <span className={`hero-sev hero-sev-${item.sev.toLowerCase()}`}>{item.sev}</span>
                  <span className="hero-issue-label">{item.label}</span>
                  <span className={`hero-status hero-status-${item.status.replace(' ', '-').toLowerCase()}`}>{item.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section className="landing-section landing-features-section" id="features">
        <div className="landing-container">
          <h2 className="landing-section-title">Everything your team needs to ship great software</h2>
          <p className="landing-section-sub">A complete toolkit for agile bug tracking, project management, and team collaboration — with zero bloat.</p>
          <div className="landing-features-grid">
            {features.map((f, i) => (
              <div key={i} className="landing-feature-card">
                <div className="landing-feature-icon">{f.icon}</div>
                <h3 className="landing-feature-title">{f.title}</h3>
                <p className="landing-feature-body">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────── */}
      <section className="landing-section landing-steps-section" id="how-it-works">
        <div className="landing-container">
          <h2 className="landing-section-title">Up and running in minutes</h2>
          <p className="landing-section-sub">No complex setup. No lengthy onboarding. Just sign up and start tracking.</p>
          <div className="landing-steps">
            {steps.map((s, i) => (
              <div key={i} className="landing-step">
                <div className="landing-step-num">{s.num}</div>
                <h3 className="landing-step-title">{s.title}</h3>
                <p className="landing-step-body">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA banner ───────────────────────────────────────── */}
      <section className="landing-cta-banner">
        <div className="landing-container landing-cta-inner">
          <h2 className="landing-cta-title">Start tracking bugs today — it's free</h2>
          <p className="landing-cta-body">Join agile teams using Naipeak Issue Tracker to ship software faster and with fewer defects.</p>
          <div className="landing-cta-group">
            <Link to="/signup" className="landing-btn-primary">Create free account</Link>
            <Link to="/login"  className="landing-btn-ghost">Log in to your workspace</Link>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-grid">
            {/* Brand column */}
            <div className="footer-col footer-col-brand">
              <div className="footer-brand-logo">
                <BrandLogo />
                <span>Naipeak Issue Tracker</span>
              </div>
              <p className="footer-brand-tagline">
                The agile bug tracking and project management platform built for modern software development teams. Free. Fast. Powerful.
              </p>
              <a
                href="https://wa.me/254797451014"
                className="footer-whatsapp-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Chat on WhatsApp
              </a>
            </div>

            {/* Product column */}
            <div className="footer-col">
              <h4 className="footer-col-title">Product</h4>
              <ul className="footer-links">
                <li><a href="#features">Features</a></li>
                <li><a href="#how-it-works">How It Works</a></li>
                <li><Link to="/signup">Sign Up Free</Link></li>
                <li><Link to="/login">Log In</Link></li>
              </ul>
            </div>

            {/* Support column */}
            <div className="footer-col">
              <h4 className="footer-col-title">Support</h4>
              <ul className="footer-links">
                <li><Link to="/help">Help Center</Link></li>
                <li><Link to="/about">About</Link></li>
                <li><Link to="/help#faq">FAQ</Link></li>
                <li>
                  <a href="mailto:info@naipeak.com">info@naipeak.com</a>
                </li>
              </ul>
            </div>

            {/* Contact column */}
            <div className="footer-col">
              <h4 className="footer-col-title">Get in Touch</h4>
              <p className="footer-contact-desc">
                Have a question or need help? We're here for you — reach out any time.
              </p>
              <div className="footer-contact-btns">
                <button className="footer-help-btn" onClick={() => setHelpOpen(true)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Request Help
                </button>
                <a
                  href="https://wa.me/254797451014"
                  className="footer-wa-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom">
          <div className="landing-container footer-bottom-inner">
            <p className="footer-bottom-copy">
              © {new Date().getFullYear()} Naipeak Issue Tracker. All rights reserved.
            </p>
            <a
              href="https://naipeak.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-powered-by"
            >
              Powered by{' '}
              <strong>Naipeak ICT</strong>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
